import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, Menu, Phone, Mail, X } from "lucide-react";
import { Logo } from "./Logo";
import { NAV, CONTACT } from "../../data/site";

const MobileMenu = ({ onClose }) => (
  <motion.div
    className="fixed inset-0 z-[45] flex flex-col bg-ink px-6 pb-10 pt-28 text-canvas md:hidden"
    initial={{ clipPath: "inset(0 0 100% 0)" }}
    animate={{ clipPath: "inset(0 0 0% 0)" }}
    exit={{ clipPath: "inset(0 0 100% 0)" }}
    transition={{ duration: 0.6, ease: [0.76, 0, 0.24, 1] }}
    data-testid="mobile-menu"
  >
    <nav className="flex flex-col gap-1" aria-label="Mobile">
      {[{ to: "/", label: "Home" }, ...NAV].map((l, i) => (
        <motion.div key={l.to} initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 + i * 0.05 }}>
          <NavLink to={l.to} onClick={onClose} data-testid={`mobile-nav-${l.label.toLowerCase()}`}
            className={({ isActive }) => `block py-2 font-display text-5xl font-bold tracking-tight ${isActive ? "text-flame" : "text-canvas"}`}>
            {l.label}
          </NavLink>
        </motion.div>
      ))}
    </nav>
    <div className="mt-auto grid grid-cols-2 gap-3">
      <a href={CONTACT.phoneHref} className="flex items-center justify-center gap-2 rounded-full border border-canvas/25 py-3 text-sm font-semibold" data-testid="mobile-menu-call"><Phone className="h-4 w-4" /> Call</a>
      <a href={`mailto:${CONTACT.email}`} className="flex items-center justify-center gap-2 rounded-full bg-flame py-3 text-sm font-semibold" data-testid="mobile-menu-email"><Mail className="h-4 w-4" /> Email</a>
    </div>
  </motion.div>
);

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  return (
    <>
      <header className={`sticky top-0 z-50 transition-[background-color,box-shadow,border-color] duration-300 ${open ? "bg-ink" : scrolled ? "border-b border-ink/10 bg-canvas/85 backdrop-blur-xl" : "bg-canvas"}`} data-testid="site-navbar">
        <div className="wrap flex h-[72px] items-center justify-between">
          <Link to="/" aria-label="The Catalyst home" data-testid="nav-logo"><Logo dark={open} /></Link>
          <nav className="hidden items-center gap-1 md:flex" aria-label="Main">
            {NAV.map((l) => (
              <NavLink key={l.to} to={l.to} data-testid={`nav-${l.label.toLowerCase()}`}
                className={({ isActive }) => `relative rounded-full px-4 py-2 text-sm font-medium transition-colors ${isActive ? "text-flame" : "text-ink/80 hover:text-ink"}`}>
                {l.label}
              </NavLink>
            ))}
            <Link to="/contact" className="btn-primary ml-3 !py-2.5" data-testid="nav-discovery-cta">
              Book a discovery call <ArrowUpRight className="h-4 w-4" />
            </Link>
          </nav>
          <button onClick={() => setOpen((v) => !v)} aria-label={open ? "Close menu" : "Open menu"} aria-expanded={open}
            className={`grid h-11 w-11 place-items-center rounded-full md:hidden ${open ? "bg-canvas text-ink" : "bg-ink text-canvas"}`} data-testid="nav-hamburger">
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </header>
      <AnimatePresence>{open && <MobileMenu onClose={() => setOpen(false)} />}</AnimatePresence>
    </>
  );
};
